import { useCallback, useEffect, useRef, useState } from "react";
import {
    FieldScoreApiError,
    SCORABLE_FIELDS,
    scoreField,
    type FieldAlignmentResult,
    type FieldEvaluationIdentity,
    type FieldFinding,
    type FieldScoreResult,
    type ScoreDraftIdentity,
    type ScorableField
} from "./qualityApi";
import { fieldEvaluationCacheKey } from "./fieldEvaluationPlan";

const AUTO_EVALUATE_DELAY_MS = 4_000;

export interface ScoredFieldQuality {
    status: "scored";
    score: number;
    feedback: string;
    findings?: FieldFinding[];
    alignment?: FieldAlignmentResult;
    evaluation?: FieldEvaluationIdentity;
    notice?: {
        kind: "updating" | "update_failed";
        message: string;
    };
}

export type FieldQuality =
    | { status: "idle" }
    | { status: "scoring" }
    | ScoredFieldQuality
    | { status: "error"; message: string };

const IDLE: FieldQuality = { status: "idle" };

interface DesiredEvaluation {
    value: string;
    cacheKey: string;
    generation: number;
    dueAt: number;
}

export interface HydratedFieldEvaluation {
    field: ScorableField;
    value: string;
    dependencyKey: string;
    complete: boolean;
    result: FieldScoreResult;
}

function scoredQualityFromResult(
    result: FieldScoreResult,
    notice?: ScoredFieldQuality["notice"]
): ScoredFieldQuality {
    const qualityResult = result.quality ?? {
        score: result.score,
        feedback: result.feedback,
        findings: []
    };
    return {
        status: "scored",
        score: qualityResult.score,
        feedback: qualityResult.feedback,
        ...(qualityResult.findings.length > 0 ? { findings: qualityResult.findings } : {}),
        ...(result.alignment
            ? { alignment: result.alignment }
            : notice?.kind === "updating"
                ? {
                    alignment: {
                        status: "unavailable" as const,
                        score: null,
                        feedback: notice.message,
                        findings: []
                    }
                }
                : {}),
        ...(result.evaluation ? { evaluation: result.evaluation } : {}),
        ...(notice ? { notice } : {})
    };
}

const RELATED_CONTEXT_UPDATING: NonNullable<ScoredFieldQuality["notice"]> = {
    kind: "updating",
    message: "Related context changed. Alignment is being reevaluated."
};

/** Scores Discover fields on blur/inference or after four seconds of idle input.
 *
 * Each field has one convergent worker. A value changed during an in-flight
 * evaluation supersedes (but does not race) the old value; the old response is
 * ignored and the worker evaluates the newest value. Draft-version conflicts
 * are retried because they are ordinary concurrent-save races, while genuine
 * provider failures remain visible and require an explicit retry/edit. */
export function useFieldQuality(
    token: string | null | undefined,
    options: {
        enabled?: boolean;
        ensurePersisted?: (
            field: ScorableField,
            value: string,
            forceSave?: boolean,
            staleDraftVersion?: number
        ) => Promise<ScoreDraftIdentity>;
        onEvaluationComplete?: (
            field: ScorableField,
            draft: ScoreDraftIdentity,
            result: FieldScoreResult
        ) => void | Promise<void>;
        onEvaluationFailed?: (
            field: ScorableField,
            draft: ScoreDraftIdentity
        ) => void | Promise<void>;
    } = {}
) {
    const enabled = options.enabled ?? true;
    const [quality, setQuality] = useState<Record<ScorableField, FieldQuality>>(() => Object.fromEntries(
        SCORABLE_FIELDS.map(field => [field, IDLE])
    ) as Record<ScorableField, FieldQuality>);
    const requests = useRef<Partial<Record<ScorableField, AbortController>>>({});
    const workers = useRef<Partial<Record<ScorableField, Promise<boolean>>>>({});
    const timers = useRef<Partial<Record<ScorableField, ReturnType<typeof setTimeout>>>>({});
    const desired = useRef<Partial<Record<ScorableField, DesiredEvaluation>>>({});
    const generations = useRef<Record<ScorableField, number>>(Object.fromEntries(
        SCORABLE_FIELDS.map(field => [field, 0])
    ) as Record<ScorableField, number>);
    const scoredKey = useRef<Partial<Record<ScorableField, string>>>({});
    const scoredValue = useRef<Partial<Record<ScorableField, string>>>({});
    const mounted = useRef(true);

    const abort = useCallback((field: ScorableField) => {
        requests.current[field]?.abort();
        delete requests.current[field];
        const timer = timers.current[field];
        if (timer !== undefined) clearTimeout(timer);
        delete timers.current[field];
    }, []);

    useEffect(() => {
        const inFlight = requests.current;
        const scheduled = timers.current;
        mounted.current = true;
        return () => {
            mounted.current = false;
            Object.values(inFlight).forEach(controller => controller?.abort());
            Object.values(scheduled).forEach(timer => timer !== undefined && clearTimeout(timer));
        };
    }, []);

    const invalidate = useCallback(
        (field: ScorableField) => {
            abort(field);
            generations.current[field] += 1;
            delete desired.current[field];
            delete scoredKey.current[field];
            delete scoredValue.current[field];
            setQuality(current => (current[field].status === "idle" ? current : { ...current, [field]: IDLE }));
        },
        [abort]
    );

    const runWorker = useCallback((field: ScorableField): Promise<boolean> => {
        const existing = workers.current[field];
        if (existing) return existing;

        const worker = (async (): Promise<boolean> => {
            let forcePersistence = false;
            let staleDraftVersion: number | undefined;
            while (mounted.current && enabled && token) {
                const target = desired.current[field];
                if (!target || target.value === "" || target.dueAt > Date.now()) return false;
                if (scoredKey.current[field] === target.cacheKey) return true;

                const controller = new AbortController();
                let evaluatedDraft: ScoreDraftIdentity | undefined;
                requests.current[field] = controller;
                setQuality(current => {
                    const fieldQuality = current[field];
                    if (fieldQuality.status === "scored" && scoredValue.current[field] === target.value) {
                        return current;
                    }
                    return { ...current, [field]: { status: "scoring" } };
                });
                try {
                    const draft = forcePersistence
                        ? await options.ensurePersisted?.(field, target.value, true, staleDraftVersion)
                        : await options.ensurePersisted?.(field, target.value, false);
                    evaluatedDraft = draft;
                    forcePersistence = false;
                    staleDraftVersion = undefined;
                    if (controller.signal.aborted) {
                        const latest = desired.current[field];
                        if (latest && latest.generation !== target.generation && latest.dueAt <= Date.now()) continue;
                        return false;
                    }
                    const result = await scoreField(field, target.value, token, { signal: controller.signal, draft });
                    if (controller.signal.aborted) {
                        const latest = desired.current[field];
                        if (latest && latest.generation !== target.generation && latest.dueAt <= Date.now()) continue;
                        return false;
                    }

                    const latest = desired.current[field];
                    if (!latest || latest.generation !== target.generation || latest.value !== target.value) {
                        // The value changed while the request was running. Only
                        // continue immediately when the newest value is due;
                        // otherwise its four-second idle timer owns the retry.
                        continue;
                    }
                    scoredKey.current[field] = target.cacheKey;
                    scoredValue.current[field] = target.value;
                    setQuality(current => ({
                        ...current,
                        [field]: scoredQualityFromResult(result)
                    }));
                    if (evaluatedDraft) {
                        void options.onEvaluationComplete?.(field, evaluatedDraft, result);
                    }
                    return true;
                } catch (error) {
                    if (controller.signal.aborted) {
                        const latest = desired.current[field];
                        if (latest && latest.generation !== target.generation && latest.dueAt <= Date.now()) continue;
                        return false;
                    }
                    const latest = desired.current[field];
                    if (!latest || latest.generation !== target.generation || latest.value !== target.value) {
                        continue;
                    }
                    if (
                        error instanceof FieldScoreApiError &&
                        error.status === 409 &&
                        error.code === "DRAFT_VERSION_STALE"
                    ) {
                        // A concurrent autosave changed only the draft revision.
                        // Force one serialized save to obtain the authoritative
                        // latest revision, yield briefly, and retry this exact
                        // value without a fixed retry limit.
                        forcePersistence = true;
                        staleDraftVersion = evaluatedDraft?.draftVersion;
                        await new Promise(resolve => setTimeout(resolve, 100));
                        continue;
                    }
                    if (evaluatedDraft) {
                        // Quality can have been persisted successfully before
                        // alignment failed. Reload those canonical components
                        // before deciding whether any feedback is unavailable.
                        try {
                            await options.onEvaluationFailed?.(field, evaluatedDraft);
                        } catch {
                            // A failed read must not hide the original provider
                            // failure or discard feedback already on the form.
                        }
                        const currentTarget = desired.current[field];
                        if (!currentTarget || currentTarget.generation !== target.generation || currentTarget.value !== target.value) {
                            continue;
                        }
                    }
                    setQuality(current => {
                        const fieldQuality = current[field];
                        if (fieldQuality.status === "scored" && scoredValue.current[field] === target.value) {
                            return {
                                ...current,
                                [field]: {
                                    ...fieldQuality,
                                    notice: {
                                        kind: "update_failed",
                                        message: "Evaluation could not update. Showing the latest valid field quality while alignment waits to update."
                                    }
                                }
                            };
                        }
                        return {
                            ...current,
                            [field]: {
                                status: "error",
                                message: error instanceof Error ? error.message : "Evaluation failed. Try again."
                            }
                        };
                    });
                    return false;
                } finally {
                    if (requests.current[field] === controller) delete requests.current[field];
                }
            }
            return false;
        })();
        workers.current[field] = worker;
        void worker.finally(() => {
            if (workers.current[field] === worker) delete workers.current[field];
        });
        return worker;
    }, [enabled, options.ensurePersisted, options.onEvaluationComplete, options.onEvaluationFailed, token]);

    const requestEvaluation = useCallback(
        (field: ScorableField, value: string, delayMs: number, dependencyKey = ""): Promise<boolean> => {
            const answer = value.trim();
            const cacheKey = fieldEvaluationCacheKey(answer, dependencyKey);
            const existingTimer = timers.current[field];

            if (!enabled || !token || answer === "") {
                if (existingTimer !== undefined) clearTimeout(existingTimer);
                delete timers.current[field];
                generations.current[field] += 1;
                delete desired.current[field];
                delete scoredKey.current[field];
                delete scoredValue.current[field];
                setQuality(current => (current[field].status === "idle" ? current : { ...current, [field]: IDLE }));
                return Promise.resolve(false);
            }
            if (scoredKey.current[field] === cacheKey) return Promise.resolve(true);

            const requestedDueAt = Date.now() + delayMs;
            const existingDesired = desired.current[field];
            if (
                existingDesired?.cacheKey === cacheKey &&
                existingDesired.dueAt <= requestedDueAt
            ) {
                // An immediate blur/infer/delete evaluation may be followed by
                // the generic render-driven debounce for the same canonical
                // value. Never let that later schedule supersede or postpone
                // the request that is already due or in flight.
                return delayMs === 0
                    ? runWorker(field).then(() => scoredKey.current[field] === cacheKey)
                    : Promise.resolve(false);
            }

            if (delayMs === 0 && existingDesired?.value !== answer) {
                // Discrete actions such as removing multiple Personas should
                // not wait for an obsolete network evaluation to finish. The
                // worker observes the new generation and immediately moves to
                // the latest canonical list after the abort.
                requests.current[field]?.abort();
            }

            if (existingTimer !== undefined) clearTimeout(existingTimer);
            delete timers.current[field];

            const generation = generations.current[field] + 1;
            generations.current[field] = generation;
            desired.current[field] = { value: answer, cacheKey, generation, dueAt: requestedDueAt };
            delete scoredKey.current[field];
            setQuality(current => {
                const fieldQuality = current[field];
                if (fieldQuality.status === "scored" && scoredValue.current[field] === answer) {
                    return {
                        ...current,
                        [field]: {
                            ...fieldQuality,
                            alignment: {
                                status: "unavailable",
                                score: null,
                                feedback: RELATED_CONTEXT_UPDATING.message,
                                findings: []
                            },
                            notice: RELATED_CONTEXT_UPDATING
                        }
                    };
                }
                delete scoredValue.current[field];
                return fieldQuality.status === "idle" ? current : { ...current, [field]: IDLE };
            });

            if (delayMs === 0) return runWorker(field).then(() => scoredKey.current[field] === cacheKey);
            timers.current[field] = setTimeout(() => {
                delete timers.current[field];
                void runWorker(field);
            }, delayMs);
            return Promise.resolve(false);
        },
        [enabled, runWorker, token]
    );

    const evaluateAndWait = useCallback(
        (field: ScorableField, value: string, dependencyKey?: string): Promise<boolean> => requestEvaluation(field, value, 0, dependencyKey),
        [requestEvaluation]
    );

    const evaluate = useCallback(
        (field: ScorableField, value: string, dependencyKey?: string) => {
            void evaluateAndWait(field, value, dependencyKey);
        },
        [evaluateAndWait]
    );

    const schedule = useCallback(
        (field: ScorableField, value: string, dependencyKey?: string) => {
            void requestEvaluation(field, value, AUTO_EVALUATE_DELAY_MS, dependencyKey);
        },
        [requestEvaluation]
    );

    const hydrate = useCallback((evaluations: HydratedFieldEvaluation[]) => {
        if (!mounted.current) return;
        const nextByField = new Map<ScorableField, ScoredFieldQuality>();
        for (const evaluation of evaluations) {
            const value = evaluation.value.trim();
            if (!value) continue;
            const cacheKey = fieldEvaluationCacheKey(value, evaluation.dependencyKey);
            scoredValue.current[evaluation.field] = value;
            if (evaluation.complete) {
                abort(evaluation.field);
                generations.current[evaluation.field] += 1;
                delete desired.current[evaluation.field];
                scoredKey.current[evaluation.field] = cacheKey;
            } else {
                delete scoredKey.current[evaluation.field];
            }
            nextByField.set(
                evaluation.field,
                scoredQualityFromResult(
                    evaluation.result,
                    evaluation.complete ? undefined : RELATED_CONTEXT_UPDATING
                )
            );
        }
        if (nextByField.size === 0) return;
        setQuality(current => {
            const next = { ...current };
            nextByField.forEach((fieldQuality, field) => {
                next[field] = fieldQuality;
            });
            return next;
        });
    }, [abort]);

    return { quality, evaluate, evaluateAndWait, schedule, invalidate, hydrate };
}
